import React from "react";
import cx from "classnames";
import {
  SpreadsheetTableDefaultDataItem,
  SpreadsheetTableColumn,
  SpreadsheetTableColumnType,
  SpreadsheetTableColumnId,
  SpreadsheetTableSelectionState,
  SPREADSHEET_TABLE_CONSTANTS,
  SpreadsheetTableColumnName,
} from "../../../../../../table.types";
import { FormControl } from "@common/components/form-control";
import { Ellipsis } from "@common/components/Ellipsis";
import "./cell.styles.scss";
import DataConverter, { addBlankOptionToDropdownOptions } from "@modules/spreadsheets/pages/spreadsheet-details/components/spreadsheet-result/components/table/data-converter";
import { createPortal } from "react-dom";
import { isSystemDateField } from "../../../../../../filter-utils";
import { getCellBorderPosition, isRowSelected, RowSelectionState } from "../../../../../../utils/selection.utils";
import moment from "moment/moment";
import { HyperlinkEditor } from "@common/components/hyperlink/HyperlinkEditor";
import { UploadModal } from "@common/components/upload-modal/UploadModal";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { LookupTableDropdown } from "./LookupTableDropdown";
import { LookupTableInput } from "./LookupTableInput";
import { ImageUploadInput } from "./ImageUploadInput";
import { openDocument, downloadDocument, uploadDocument, renameDocument } from "@common/actions/table";
import { MessageBox } from "@common/components/MessageBox";
import { useMessage } from "@common/hooks/message";
import Select from "react-select";
import { validateSpreadsheetDateInput, getSpreadsheetInvalidDateMessageContent, DATE_PICKER_FORMAT, isValidDate } from "@modules/datasheets/utils/date-validation";
import { I18n } from "react-redux-i18n";

/**
 * Props for the Cell component
 */
export type CellProps = {
  className?: string;
  dataItem: SpreadsheetTableDefaultDataItem;
  column: SpreadsheetTableColumn;
  columnWidth: number;
  columnIds: Array<SpreadsheetTableColumnId>;
  spreadsheetId?: number;
  uiColumnId: number;
  rowIndex: number;
  selectionState: SpreadsheetTableSelectionState;
  rowSelectionState?: RowSelectionState;
  onCellMouseDown: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    dataItem: SpreadsheetTableDefaultDataItem,
    columnName: SpreadsheetTableColumnName,
    event?: React.MouseEvent
  ) => void;
  onCellMouseEnter: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId
  ) => void;
  onCellMouseUp: () => void;
  isCellSelected: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId
  ) => boolean;
  allColumns?: SpreadsheetTableColumn[]; // Add this to access all columns for color logic
  onCellValueChange?: (
    rowIndex: number,
    columnId: SpreadsheetTableColumnId,
    value: string | number,
    popPreviousUndo?: boolean
  ) => void;
  showMessage?: (config: { header?: string; content: React.ReactNode; size?: 'small' | 'medium' }) => void;
  setCellToUpdate: React.Dispatch<React.SetStateAction<{ rowIndex: number, columnId: number, newValue: string | number } | null>>;
  onActivateSpreadsheet?: () => void;
};

export const Cell: React.FC<CellProps> = React.memo((props) => {
  const {
    className,
    dataItem,
    column,
    columnWidth,
    columnIds,
    uiColumnId,
    rowIndex,
    selectionState,
    rowSelectionState,
    onCellMouseDown,
    onCellMouseEnter,
    onCellMouseUp,
    isCellSelected,
    allColumns = [],
    onCellValueChange,
    setCellToUpdate,
    onActivateSpreadsheet,
  } = props;

  // Extract raw value from data item using column's accessor key
  const rawValue = column.accessorKey ? dataItem[column.accessorKey] : "";
  const cellInvalidKey = column.accessorKey ? `${column.accessorKey}_invalid` : null;
  const cellRequiredKey = column.accessorKey ? `${column.accessorKey}_required` : null;
  const cellServerInvalidKey = column.accessorKey
    ? `${column.accessorKey}_serverInvalid`
    : null;
  const isSelected = isCellSelected(rowIndex, uiColumnId);
  const { showMessage, messageProps, closeMessage, showErrorMessage, showConfirmMessage } = useMessage();
  const [datePickerRawValue, setDatePickerRawValue] = React.useState<string | Date | null>("");


  /**
   * Check if this cell has any data at all (including empty strings)
   * This determines whether to render content or leave cell empty
   */
  const hasAnyData = React.useMemo(() => {
    // If no accessorKey, no data
    if (!column.accessorKey) return false;

    // Check if the property exists in dataItem (even if it's null, undefined, or empty string)
    return column.accessorKey in dataItem;
  }, [column.accessorKey, dataItem]);

  // Access extended column fields safely with proper typing
  const typeId: number | undefined = column.typeId;
  // const fieldType: string | undefined = column.fieldType;
  const parentFieldName: string | undefined = column.parentFieldName;
  const dataDropdownOptions:
    | Array<{ actualValue?: string | number; displayValue: string }>
    | undefined = column.dataDropdownOptions;
  // Consider both per-row and per-column flags, and require that the cell actually has data
  const baseEditable: boolean = (dataItem as any)?.editable ?? column.editable ?? false;

  /**
   * Cell-level permission checking:
   * Each cell has its own permission stored in dataItem under the key pattern: `${accessorKey}_permissions`
   * Permissions are set by mapApiDataToRows() in table.controller.tsx from cell.field.permissions
   * Values can be "WRITE" (editable) or "READ" (read-only, grayed out)
   */
  // Construct the permission key for this cell: e.g., "fieldName__f123_permissions" or "tableId__fieldName__f123_permissions"
  const cellPermissionKey = column.accessorKey ? `${column.accessorKey}_permissions` : null;
  // Retrieve the cell-specific permission from dataItem (may be "WRITE", "READ", null, or undefined)
  const cellPermission = cellPermissionKey ? (dataItem as any)?.[cellPermissionKey] : null;
  // Determine if user has write permission: only "WRITE" (case-insensitive) grants edit access
  // If permission is missing/null/undefined, default to read-only (false)
  const hasWritePermission: boolean = cellPermission ? cellPermission.toUpperCase() === "WRITE" : false;

  /**
   * Final editability determination:
   * A cell is editable only when ALL of the following conditions are met:
   * 1. baseEditable is true (field type allows editing AND row/column flags permit it)
   * 2. hasWritePermission is true (user has WRITE permission for this specific cell)
   * 3. The cell has data (column.accessorKey exists in dataItem)
   */
  const isEditable: boolean = Boolean(baseEditable && hasWritePermission && (column.accessorKey ? (column.accessorKey in dataItem) : false));

  /**
   * Special case for dropdown cells:
   * Dropdowns can be opened even if the cell is empty (no data key exists),
   * allowing users to select a value for empty cells. Still requires baseEditable and WRITE permission.
   */
  const isDropdownEditable: boolean = Boolean(baseEditable && hasWritePermission && column.typeId === 5);
  const isDateEditable: boolean = Boolean(baseEditable && hasWritePermission && column.typeId === 2);
  const isLookupEditable: boolean = Boolean(
    baseEditable && hasWritePermission && column.typeId === 7 &&
    column.lookupValues && Object.keys(column.lookupValues).length > 0
  );
  const canEnterEditOnEnterKey: boolean = Boolean(baseEditable && hasWritePermission && column.accessorKey != null);
  const modulo: number | undefined = column.modulo;

  /**
   * Convert value for display if constants exist for this field
   * Uses DataConverter to transform raw values to display-friendly format
   */
  const displayValue: any = React.useMemo(() => {
    // Special handling for row count column - always show the raw value
    if (column.id === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID) {
      return rawValue;
    }

    // Special handling for Image fields - show filename if available
    if (column.typeId === 6 && rawValue) {
      // If rawValue is an object with a name property, extract the filename
      return rawValue.name;
    }

    // Return empty string if no data
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return "";
    }

    // Date handling
    if (column.system && isSystemDateField(column.id)) {
      return DataConverter.formatDateForInput(rawValue);
    }
    if (column.typeId === 2) {
      return DataConverter.formatDateForInput(rawValue);
    }

    // Use backend displayValue if available (stored in separate _display key)
    if (column.accessorKey) {
      const displayValueKey = `${column.accessorKey}_display`;
      const backendDisplayValue = dataItem?.[displayValueKey];
      if (backendDisplayValue !== undefined && backendDisplayValue !== null && backendDisplayValue !== "") {
        return backendDisplayValue;
      }
    }

    // Fallback to frontend conversion for system fields only
    if (column.system && column.id) {
      return DataConverter.toDisplayById(column.id, rawValue);
    }

    return rawValue;
  }, [rawValue, column.typeId, column.system, column.id, column.accessorKey, dataItem]);

  // Sync datePickerRawValue when the underlying display value changes (e.g., after API save or initial load)
  React.useEffect(() => {
    if (column.typeId === 2 && displayValue !== undefined) {
      setDatePickerRawValue(displayValue || "");
    }
  }, [column.typeId, displayValue]);

  /**
   * Handle dropdown value change
   * TODO: Implement data update logic here for saving changes
   */
  const handleDropdownChange = (newValue: string) => {
    if (onCellValueChange) {
      onCellValueChange(rowIndex, uiColumnId, newValue);
    }
  };

  /**
   * Handle text input change
   */
  const handleTextChange = (newValue: string) => {
    setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
  };

  /**
   * Handle date change
   * TODO: Implement data update logic here for saving changes
   */
  const handleDateChange = (newValue: string) => {
    if (onCellValueChange) {
      onCellValueChange(rowIndex, uiColumnId, newValue);
    }
  };

  // Add a function to convert date to HTML date input format (YYYY-MM-DD)
  const getHtmlDateValue = React.useMemo(() => {
    if (!rawValue || typeof rawValue !== "string") return "";

    // Convert YYYY/MM/DD to YYYY-MM-DD for HTML date input
    const momentDate = moment(rawValue, "YYYY/MM/DD");
    return momentDate.isValid() ? momentDate.format("YYYY-MM-DD") : "";
  }, [rawValue]);
  /**
   * Handle checkbox change - converts boolean to Y/N format
   * TODO: Implement data update logic here for saving changes
   */
  const handleCheckboxChange = React.useCallback((checked: boolean) => {
    const newValue = checked ? "Y" : "N";
    if (onCellValueChange) {
      onCellValueChange(rowIndex, uiColumnId, newValue);
    }
  }, [onCellValueChange, rowIndex, uiColumnId]);

  /**
   * Check if value represents a checked checkbox (Y = checked, N = unchecked)
   */
  const isCheckboxChecked = React.useMemo(() => {
    if (rawValue === null || rawValue === undefined || rawValue === "") {
      return false;
    }
    const value = String(rawValue).toUpperCase();
    return value === "Y"; // Only Y means checked, everything else (N, empty, etc.) is unchecked
  }, [rawValue]);

  /**
   * Handle date validation and save
   * Validates the date input and saves it if valid, shows error message if invalid
   * @param dateValue - The date value to validate (can be Date object or null)
   * @param dateString - The date string to validate (can be string in MM/DD/YYYY format)

   */
  const handleDateValidationAndSave = (dateValue: string | Date | null, dateString?: string | null) => {
    let mmddyyyyFormat: string;

    // Handle Date object from DatePicker
    if (dateString) {
      mmddyyyyFormat = dateString

    } else {
      if (dateValue instanceof Date) {
        mmddyyyyFormat =
          String(dateValue.getUTCMonth() + 1).padStart(2, '0') + '/' +
          String(dateValue.getDate()).padStart(2, '0') + '/' +
          dateValue.getFullYear();

      } else {
        mmddyyyyFormat = dateValue as string;

      }
    }

    // Validate the date format
    const validation = validateSpreadsheetDateInput(mmddyyyyFormat);

    if (validation.isValid && validation.normalizedValue !== undefined) {
      // Valid date - save it
      if (onCellValueChange) {
        onCellValueChange(rowIndex, uiColumnId, validation.normalizedValue);
      }
      setForceOpenDate(false);
    } else {
      // Invalid date - show error message
      if (showMessage) {
        showMessage({
          header: informationMessageHeader,
          content: getSpreadsheetInvalidDateMessageContent(),
          size: 'small',
        });
      }
    }
  }

  /**
   * Check if this is a checkbox field
   */
  const isCheckboxField = React.useMemo(() => {

    return column.typeId === 4;
  }, [column.typeId]);



  /**
   * Check if this is a number field
   */
  const isNumberField = React.useMemo(() => {
    return column.typeId === 3;
  }, [column.typeId]);

  /**
   * Check if this is a lookup table field
   */
  const isLookupTableField = React.useMemo(() => {
    return (
      column.typeId === 7
      &&
      column.lookupValues &&
      Object.keys(column.lookupValues).length > 0
    );
  }, [column.typeId, column.lookupValues]);


  const cellFieldTypeId = column.accessorKey
    ? (dataItem as any)?.[`${column.accessorKey}_fieldTypeId`]
    : undefined;
  const isImageField = React.useMemo(() => {
    return column.typeId === 6 || cellFieldTypeId === 6;
  }, [column.typeId, cellFieldTypeId]);

  // Build preview URL for images (used for hover/preview/lightbox)
  const imagePreviewUrl: string | null = React.useMemo(() => {
    if (!isImageField) return null;
    if (!displayValue) return null;
    if (!dataItem.globalId || !dataItem.fieldContext || !column.id) return null;
    const tableId = (column as any)?.parentField?.fieldid || 0;
    const params = new URLSearchParams({
      fieldId: String(column.id),
      fieldContext: String(dataItem.fieldContext),
      objectId: String(dataItem.globalId),
      tableId: String(tableId),
      rrn: String((dataItem as any)?.rrn || 0),
    });
    return `api/documents/open/${encodeURIComponent(String(displayValue))}?${params.toString()}`;
  }, [isImageField, displayValue, dataItem.globalId, dataItem.fieldContext, (column as any)?.parentField, column.id, (dataItem as any)?.rrn]);

  /**
   * Validate number field value
   */
  const validateNumberValue = React.useCallback(
    (value: string): { isValid: boolean; errorMessage?: string } => {
      if (!value || value === "") {
        return { isValid: true }; // Empty values are valid
      }

      const normalized = value.trim();
      let decimalCount = 0;
      let hasDigit = false;
      for (let i = 0; i < normalized.length; i++) {
        const char = normalized.charAt(i);
        if (char === ".") {
          decimalCount += 1;
          if (decimalCount > 1) {
            return {
              isValid: false,
              errorMessage:
                numberValidationMessage,
            };
          }
          continue;
        }
        if (char === "-") {
          if (i !== 0) {
            return {
              isValid: false,
              errorMessage:
                numberValidationMessage,
            };
          }
          continue;
        }
        if (char < "0" || char > "9") {
          return {
            isValid: false,
            errorMessage:
              numberValidationMessage,
          };
        }
        hasDigit = true;
      }

      if (!hasDigit) {
        return {
          isValid: false,
          errorMessage:
            numberValidationMessage,
        };
      }

      return { isValid: true };
    },
    []
  );

  /**
   * Handle number field value change with validation
   */
  const handleNumberChange = React.useCallback(
    (value: string) => {
      const validation = validateNumberValue(value);
      if (validation.isValid) {
        setNumberValidationError(null);
        // Handle empty values properly - don't convert empty string to 0
        const newValue = value.trim() === "" ? "" : Number(value);
        setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
      } else {
        setNumberValidationError(
          validation.errorMessage || I18n.t("Invalid number")
        );
        setCellToUpdate(null);
      }
    },
    [validateNumberValue, onCellValueChange, rowIndex, uiColumnId]
  );

  /**
   * Handle lookup table value selection
   */
  const handleLookupTableSelection = React.useCallback(
    (selectedValue: string) => {
      setShowLookupTableDropdown(false);
      setIsEditing(false);
      setCellToUpdate(null);
      if (onCellValueChange) {
        onCellValueChange(rowIndex, uiColumnId, selectedValue);
      }
    },
    [onCellValueChange, rowIndex, uiColumnId, setCellToUpdate]
  );

  // Validate current value on mount and when rawValue changes
  React.useEffect(() => {
    if (
      isNumberField &&
      rawValue !== null &&
      rawValue !== undefined &&
      rawValue !== ""
    ) {
      const validation = validateNumberValue(String(rawValue));
      if (!validation.isValid) {
        setNumberValidationError(
          validation.errorMessage || I18n.t("Invalid number")
        );
      } else {
        setNumberValidationError(null);
      }
    } else if (isNumberField) {
      // Clear error state for empty values
      setNumberValidationError(null);
    }
  }, [isNumberField, rawValue, validateNumberValue]);
  const [showLinkPopup, setShowLinkPopup] = React.useState(false);
  const [popupPos, setPopupPos] = React.useState<{
    x: number;
    y: number;
    placement: "bottom" | "top";
  }>({ x: 0, y: 0, placement: "bottom" });
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState("");
  const [editValueImage, setEditValueImage] = React.useState("");
  const [showUploadWindow, setShowUploadWindow] = React.useState(false);
  const [numberValidationError, setNumberValidationError] = React.useState<
    string | null
  >(null);
  const [showLookupTableDropdown, setShowLookupTableDropdown] =
    React.useState(false);
  const [forceOpenDate, setForceOpenDate] = React.useState(false);
  const [forceOpenDropdown, setForceOpenDropdown] = React.useState(false);
  // Guards against double-dispatch when onKeyDown (text input) and onSelect (calendar) both fire for the same Enter press.
  const dateKeyDownHandledRef = React.useRef(false);
  // Tracks shiftKey for the most recent Enter keydown while the date picker is open (capture phase fires before onSelect).
  const dateSelectShiftKeyRef = React.useRef(false);
  const selectRef = React.useRef<any>(null);
  const hyperlinkInputRef = React.useRef<HTMLInputElement | null>(null);
  const hyperlinkDraftRef = React.useRef<string>(String(displayValue ?? ""));
  const hyperlinkInitialCharRef = React.useRef<string | null>(null);
  const cellContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [numberErrorTooltipPos, setNumberErrorTooltipPos] = React.useState<{
    left: number;
    top: number;
    placement: "above" | "below";
  } | null>(null);
  const [requiredErrorTooltipPos, setRequiredErrorTooltipPos] = React.useState<{
    left: number;
    top: number;
    placement: "above" | "below";
  } | null>(null);
  const cellHasNumberValidationError = isNumberField && Boolean(numberValidationError);
  const hasBackendCellValidationError = Boolean(
    cellServerInvalidKey
      ? (dataItem as any)?.[cellServerInvalidKey]
      : cellInvalidKey
        ? (dataItem as any)?.[cellInvalidKey]
        : false
  );
  const isRawValueEmpty =
    rawValue === null ||
    rawValue === undefined ||
    (typeof rawValue === "string" && rawValue.trim() === "") ||
    (isImageField && (rawValue === 0 || rawValue === "0" ||
      (typeof rawValue === "object" && rawValue !== null && (rawValue as any).name == null)));
  const rowRequiredFlag = cellRequiredKey
    ? (dataItem as Record<string, unknown>)[cellRequiredKey]
    : undefined;
  const isFieldRequired =
    Boolean(column.required) || rowRequiredFlag === true;
  const hasRequiredFieldError = Boolean(isFieldRequired && isRawValueEmpty);
  const hasCellValidationError =
    cellHasNumberValidationError || hasBackendCellValidationError || hasRequiredFieldError;
  const informationMessageHeader = I18n.t("Information Message");
  const numberValidationMessage = I18n.t(
    "Number field accepts numeric values only, please re-try."
  );
  const cellTitle = hasRequiredFieldError
    ? undefined
    : typeof displayValue === "string" || typeof displayValue === "number"
      ? displayValue.toString()
      : undefined;
  const initialCharRef = React.useRef<string | null>(null);
  const [dropdownInitialInput, setDropdownInitialInput] = React.useState('');
  const dropdownFocusTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  // Clear initialChar after it's been consumed by the first render in edit mode
  React.useEffect(() => {
    if (isEditing && initialCharRef.current != null) {
      initialCharRef.current = null;
    }
  }, [isEditing]);
  // Clear pending dropdown focus timeout on unmount (SP-12234)
  React.useEffect(() => {
    return () => {
      if (dropdownFocusTimeoutRef.current) {
        clearTimeout(dropdownFocusTimeoutRef.current);
      }
    };
  }, []);
  // Clear pending focus timeouts and blur any focused element when exiting edit mode (SP-12234)
  React.useEffect(() => {
    if (!isEditing) {
      // Clear the 50ms dropdown focus timeout to prevent it from re-focusing after exit
      if (dropdownFocusTimeoutRef.current) {
        clearTimeout(dropdownFocusTimeoutRef.current);
        dropdownFocusTimeoutRef.current = null;
      }
      // Use requestAnimationFrame to blur AFTER react-select's internal focus management
      // (react-select re-focuses its input when the menu closes, which runs during the
      // same React render cycle as our useEffect — rAF ensures we blur after that)
      if (cellContainerRef.current) {
        const container = cellContainerRef.current;
        const rafId = requestAnimationFrame(() => {
          const focused = container.querySelector(':focus') as HTMLElement | null;
          if (focused) {
            focused.blur();
          }
        });
        return () => cancelAnimationFrame(rafId);
      }
    }
  }, [isEditing]);
  // Global single-edit coordination
  const cellKey = React.useMemo(() => `${rowIndex}:${uiColumnId}`, [rowIndex, uiColumnId]);
  const notifyBeginEdit = React.useCallback(() => {
    try {
      window.dispatchEvent(
        new CustomEvent('spreadsheet:beginEdit', { detail: { key: cellKey } })
      );
    } catch { }
  }, [cellKey]);

  React.useEffect(() => {
    const handler = (e: any) => {
      const key = e?.detail?.key;
      if (key && key !== cellKey) {
        setIsEditing(false);
        setShowLookupTableDropdown(false);
        setShowLinkPopup(false);
        setShowUploadWindow(false);
        setShowImagePreview(false);
        setForceOpenDate(false);
        setForceOpenDropdown(false);
      }
    };
    window.addEventListener('spreadsheet:beginEdit', handler as any);
    return () => window.removeEventListener('spreadsheet:beginEdit', handler as any);
  }, [cellKey]);

  const updateNumberErrorTooltipPosition = React.useCallback(() => {
    if (!cellHasNumberValidationError || !cellContainerRef.current) {
      setNumberErrorTooltipPos(null);
      return;
    }
    const rect = cellContainerRef.current.getBoundingClientRect();
    const tooltipWidth = 260;
    const halfWidth = tooltipWidth / 2;
    let centerX = rect.left + rect.width / 2;
    centerX = Math.max(halfWidth + 8, Math.min(centerX, window.innerWidth - halfWidth - 8));
    const preferAbove = rect.top > 80;
    const top = preferAbove ? rect.top - 8 : rect.bottom + 8;
    setNumberErrorTooltipPos({
      left: centerX,
      top,
      placement: preferAbove ? "above" : "below",
    });
  }, [cellHasNumberValidationError]);

  React.useLayoutEffect(() => {
    if (!cellHasNumberValidationError) {
      setNumberErrorTooltipPos(null);
      return;
    }
    updateNumberErrorTooltipPosition();
    const handleWindowChange = () => updateNumberErrorTooltipPosition();
    window.addEventListener("resize", handleWindowChange);
    window.addEventListener("scroll", handleWindowChange, true);
    return () => {
      window.removeEventListener("resize", handleWindowChange);
      window.removeEventListener("scroll", handleWindowChange, true);
    };
  }, [cellHasNumberValidationError, updateNumberErrorTooltipPosition, isEditing, rowIndex, uiColumnId]);

  // Helper: compute and set lookup dropdown position for this cell
  const computeAndSetLookupPosition = React.useCallback(() => {
    try {
      const cellEl = document.querySelector(
        `[data-row-index="${rowIndex}"][data-column-id="${uiColumnId}"]`
      ) as HTMLElement | null;
      if (!cellEl) return false;
      const rect = cellEl.getBoundingClientRect();
      setPopupPos({ x: rect.left + rect.width / 2, y: rect.bottom + 5, placement: 'bottom' });
      return true;
    } catch {
      return false;
    }
  }, [rowIndex, uiColumnId]);

  // Auto-manage lookup dropdown visibility based on editing state
  React.useEffect(() => {
    if (!isLookupTableField) return;
    if (isEditing) {
      const positioned = computeAndSetLookupPosition();
      setShowLookupTableDropdown(positioned);
    } else {
      setShowLookupTableDropdown(false);
    }
  }, [isEditing, isLookupTableField, computeAndSetLookupPosition]);

  // Close lookup dropdown on outside clicks (outside both dropdown and this cell)
  React.useEffect(() => {
    if (!showLookupTableDropdown) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Element;
      const clickedInDropdown = !!target.closest('.lookup-table-dropdown');
      const clickedInCell = !!(cellContainerRef.current && target.closest('[data-row-index][data-column-id]') === cellContainerRef.current);
      if (!clickedInDropdown && !clickedInCell) {
        setShowLookupTableDropdown(false);
        // Ensure lookup cells fully exit edit mode on outside clicks (e.g., header click).
        // Without this, previous lookup cell can remain visually in edit state.
        setIsEditing(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [showLookupTableDropdown]);

  // Listen for global request to edit the currently selected cell (Enter key or type-to-edit)
  React.useEffect(() => {
    const onEditCell = (ev: any) => {
      const r = ev?.detail?.rowIndex;
      const c = ev?.detail?.columnId;
      const ssid = ev?.detail?.spreadsheetId;
      const initialChar: string | undefined = ev?.detail?.initialChar;
      if (typeof r === 'number' && typeof c === 'number') {
        if (r === rowIndex && c === uiColumnId && (ssid == null || ssid === (props.spreadsheetId as any))) {
          const isTypeToEdit = initialChar != null;
          // Block type-to-edit for checkbox and ALL lookup table fields (typeId=7),
          // regardless of whether lookupValues are populated (SP-12234)
          const skipTypeToEdit = isCheckboxField || column.typeId === 7;
          if (isTypeToEdit && skipTypeToEdit) return;
          // Enter/Shift+Enter on highlighted lookup cell: navigate to next/prev cell.
          // Lookup cells enter edit mode only via double-click; Enter navigates instead.
          if (!isTypeToEdit && column.typeId === 7) {
            const shiftKey = (ev?.detail?.shiftKey as boolean | undefined) ?? false;
            window.dispatchEvent(new CustomEvent('spreadsheet:enterFromCell', {
              detail: { rowIndex, columnId: uiColumnId, shiftKey, spreadsheetId: props.spreadsheetId as any },
            }));
            return;
          }

          const canEnterEdit = isEditable || isDropdownEditable || isDateEditable || isLookupEditable || canEnterEditOnEnterKey;
          if (!canEnterEdit && !isTypeToEdit) {
            // Non-editable cell (e.g. system field): Enter/Shift+Enter navigates instead of doing nothing
            const shiftKey = (ev?.detail?.shiftKey as boolean | undefined) ?? false;
            window.dispatchEvent(new CustomEvent('spreadsheet:enterFromCell', {
              detail: { rowIndex, columnId: uiColumnId, shiftKey, spreadsheetId: props.spreadsheetId as any },
            }));
          }
          if (canEnterEdit) {
            notifyBeginEdit();
            const isSimpleTextOrNumberTypeToEdit =
              isTypeToEdit &&
              !isImageField &&
              column.typeId !== 14 &&
              column.typeId !== 2 &&
              column.typeId !== 5;
            // For text/number type-to-edit, clear previous value and seed editor with typed character only.
            if (isSimpleTextOrNumberTypeToEdit) {
              initialCharRef.current = initialChar ?? '';
            } else {
              initialCharRef.current = initialChar ?? null;
            }
            if (isImageField) {
              setEditValueImage(initialChar ?? String(displayValue ?? ''));
            } else if (column.typeId === 14) {
              if (isTypeToEdit) {
                const shouldDeferInitialCharInsert = initialChar != null && initialChar !== '';
                hyperlinkDraftRef.current = '';
                hyperlinkInitialCharRef.current = shouldDeferInitialCharInsert ? initialChar : null;
                setEditValue('');
              } else {
                // Enter-to-edit: preserve existing value, cursor at end
                const existingValue = String(displayValue ?? '');
                hyperlinkDraftRef.current = existingValue;
                hyperlinkInitialCharRef.current = null;
                setEditValue(existingValue);
              }
            } else if (!isTypeToEdit && typeof displayValue === 'string' && displayValue.includes('@')) {
              // Enter-to-edit on email field (non-typeId-14): preserve existing value
              setEditValue(displayValue);
            }
            setIsEditing(true);
            // For text/number Enter-to-edit (no initialChar): place cursor at end of existing text.
            // autoFocus alone does not guarantee end-of-text cursor position across all browsers.
            if (!isTypeToEdit && !isImageField &&
                column.typeId !== 2 && column.typeId !== 4 && column.typeId !== 5 &&
                column.typeId !== 7 && column.typeId !== 14) {
              setTimeout(() => {
                const input = cellContainerRef.current?.querySelector('input, textarea') as HTMLInputElement | null;
                if (input) {
                  const len = input.value.length;
                  input.setSelectionRange(len, len);
                }
              }, 0);
            }
            // Open date picker automatically when entering edit via Enter (not type-to-edit)
            if (column.typeId === 2 && !isTypeToEdit) {
              setForceOpenDate(true);
            }
            // Open dropdown menu when entering edit via Enter (not type-to-edit)
            if (column.typeId === 5 && !isTypeToEdit) {
              setForceOpenDropdown(true);
            }
            // For hyperlink / image fields, focus the input after React renders
            if (column.typeId === 14 || isImageField) {
              setTimeout(() => {
                const input = cellContainerRef.current?.querySelector('input[type="text"]') as HTMLInputElement | null;
                input?.focus();
                if (column.typeId === 14 && input) {
                  const deferredChar = hyperlinkInitialCharRef.current;
                  if (deferredChar) {
                    // Insert initial character only if native typing didn't already mutate the input.
                    if (input.value === hyperlinkDraftRef.current) {
                      const updated = `${input.value}${deferredChar}`;
                      input.value = updated;
                      hyperlinkDraftRef.current = updated;
                      input.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                    hyperlinkInitialCharRef.current = null;
                  } else if (!isTypeToEdit) {
                    // enter-to-edit: position cursor at end of existing text
                    const len = input.value.length;
                    input.setSelectionRange(len, len);
                  }
                }
              }, 0);
            }
            // Handle type-to-edit for different field types
            if (isTypeToEdit) {
              if (column.typeId === 2) {
                // Date field: focus input and insert the typed character
                if (initialChar === '') {
                  handleDateChange('');
                }
                setDatePickerRawValue(initialChar);
                setTimeout(() => {
                  const input = cellContainerRef.current?.querySelector('.react-datepicker-wrapper input') as HTMLInputElement | null;
                  if (input) {
                    input.focus();
                    if (initialChar) {
                      input.select();
                      document.execCommand('insertText', false, initialChar);
                    } else {
                      input.value = '';
                    }
                  }
                }, 0);
              } else if (column.typeId === 5) {
                // Dropdown field: open menu and filter with typed character
                if (initialChar === '') {
                  handleDropdownChange('');
                } else {
                  setDropdownInitialInput(initialChar ?? '');
                  setForceOpenDropdown(true);
                }
              }
            }
            // For lookup, the isEditing effect handles opening with positioning
          }
        }
      }
    };
    window.addEventListener('spreadsheet:editCell', onEditCell as any);
    return () => window.removeEventListener('spreadsheet:editCell', onEditCell as any);
  }, [rowIndex, uiColumnId, isEditable, isDropdownEditable, isDateEditable, isLookupEditable, canEnterEditOnEnterKey, isImageField, displayValue]);

  // Capture-phase keydown listener: track shiftKey when Enter is pressed while
  // a date cell is in edit mode, so onSelect can use the correct direction.
  React.useEffect(() => {
    if (!isEditing || column.typeId !== 2) return;
    const captureShift = (e: KeyboardEvent) => {
      if (e.key === 'Enter') dateSelectShiftKeyRef.current = e.shiftKey;
    };
    document.addEventListener('keydown', captureShift, true);
    return () => document.removeEventListener('keydown', captureShift, true);
  }, [isEditing, column.typeId]);

  // Listen for spacebar on selected cell — toggle checkbox
  React.useEffect(() => {
    if (!isCheckboxField) return;
    const onSpace = (ev: any) => {
      const r = ev?.detail?.rowIndex;
      const c = ev?.detail?.columnId;
      const ssid = ev?.detail?.spreadsheetId;
      if (typeof r === 'number' && typeof c === 'number' &&
          r === rowIndex && c === uiColumnId &&
          (ssid == null || ssid === (props.spreadsheetId as any))) {
        if (isEditable) {
          handleCheckboxChange(!isCheckboxChecked);
        }
      }
    };
    window.addEventListener('spreadsheet:spaceOnCell', onSpace as any);
    return () => window.removeEventListener('spreadsheet:spaceOnCell', onSpace as any);
  }, [rowIndex, uiColumnId, isCheckboxField, isEditable, isCheckboxChecked, handleCheckboxChange]);

  // Image preview/lightbox state
  const [showImagePreview, setShowImagePreview] = React.useState(false);

  // Get border position for this cell (only for cell selection, not row selection)
  const borderPosition = React.useMemo(() => {
    // Don't show borders if this row is in row selection mode
    const isRowHighlighted = rowSelectionState && isRowSelected(rowIndex, rowSelectionState);
    if (isRowHighlighted) {
      return { top: false, right: false, bottom: false, left: false };
    }

    return getCellBorderPosition(
      rowIndex,
      uiColumnId,
      selectionState.selection,
      columnIds
    );
  }, [rowIndex, uiColumnId, selectionState.selection, columnIds, rowSelectionState]);
  const [imagePreviewPos, setImagePreviewPos] = React.useState<{ x: number; y: number; placement: "bottom" | "top" }>({ x: 0, y: 0, placement: "bottom" });
  const [showImageLightbox, setShowImageLightbox] = React.useState(false);

  /**
   * Handle file upload from UploadModal
   */
  const handleFileUpload = async (file: File) => {
    // Blacklist dangerous extensions
    if (file && /\.(exe|bat)$/i.test(file.name)) {
      const msg = I18n.t("File type not allowed: {fileName}", {
        fileName: file.name,
      });
      if (showErrorMessage) showErrorMessage(msg);
      setShowUploadWindow(false);
      return;
    }
    if (!dataItem.globalId || !dataItem.fieldContext || !column.id) {
      const errorMessage = I18n.t(
        "Missing required data for upload. Please ensure all required fields are available."
      );


      if (showMessage) {
        showMessage({
          header: I18n.t("Upload Error"),
          content: errorMessage,
          size: 'small'
        });
      }
      setShowUploadWindow(false);
      return;
    }

    const tableId = column.parentField?.fieldid || 0;
    const isImage = column.typeId === 6;

    // Helper to perform the upload with overwrite flag
    const performUpload = async (overwrite: boolean) => {
      return await uploadDocument(
        file,
        column.id,
        dataItem.fieldContext || 1,
        dataItem.globalId || 0,
        tableId,
        dataItem.rrn || 0,
        {
          overwrite,
          isImage,
          isProfilePhoto: false
        }
      );
    };

    try {
      await performUpload(false);
      // Update the cell value with the filename only on successful upload
      if (onCellValueChange) {
        onCellValueChange(rowIndex, uiColumnId, file.name);
      }

      // Set the filename in the editValue to display in editor
      if (file && file.name) {
        if (column.typeId === 6) {
          setEditValueImage(file.name);
        } else {
          setEditValue(file.name);
        }
      }



    } catch (error: any) {
      const errCode = Number(error?.code || 0);
      // Handle conflict (file exists) with confirmation
      if (errCode === 409) {
        const msg =
          error?.message ||
          I18n.t('File "{fileName}" already exists. Overwrite?', {
            fileName: file.name,
          });
        if (showConfirmMessage) {
          showConfirmMessage({
            header: I18n.t("Confirmation message"),
            size: 'small',
            onCustomOk: async () => {
              try {
                await performUpload(true);
                if (onCellValueChange) {
                  onCellValueChange(rowIndex, uiColumnId, file.name); // popPreviousUndo = true Added since undo/redo tracks every API call for cellValue change. Thus it tracked the name change for overwriting the file as well. 
                }
                if (column.typeId === 6) {
                  setEditValueImage(file.name);
                } else {
                  setEditValue(file.name);
                }
                if (closeMessage) closeMessage();
                setShowUploadWindow(false);
                if (showMessage) {
                  showMessage({
                    header: I18n.t("Upload Success"),
                    content: I18n.t('File "{fileName}" uploaded successfully.', {
                      fileName: file.name,
                    }),
                    size: 'small'
                  });
                }
              } catch (owErr: any) {
                if (showErrorMessage) {
                  showErrorMessage(owErr?.message || I18n.t("Overwrite failed"));
                }
              }
            },
            confirmText: I18n.t("Overwrite"), cancelText: I18n.t("Cancel"),
            content: msg,
          });
        }
        return;
      }


      // Show error message to user
      const errorMessage = error instanceof Error
        ? error.message
        : I18n.t("File upload failed. Please try again.");

      if (showErrorMessage) {
        showErrorMessage(errorMessage,);
      } else {
        console.error("showMessage function is not available!");
      }

    } finally {
      // Close the upload modal
      setShowUploadWindow(false);
    }
  };

  /**
   * Handle rejected files
   */
  const handleFileReject = (rejectedFiles: any) => {
    // Handle rejected files (too large, wrong type, etc.)
  };

  // Generate unique ID for this cell's link div
  const linkDivId = React.useMemo(() => {
    return `link-cell-${column.id}-${dataItem.id}-${Date.now()}`;
  }, [column.id, dataItem.id]);

  // handlers
  const handleLinkMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!displayValue) return;
    // Get the link div element by its unique ID
    const linkElement = document.getElementById(linkDivId);
    if (!linkElement) return;

    const rect = linkElement.getBoundingClientRect();

    // Position popup to overlap directly on the link div
    let y = rect.bottom - 2;
    let placement: "bottom" | "top" = "bottom";
    const estH = 34;

    // Check if popup would go below viewport
    if (y + estH > window.innerHeight) {
      y = rect.top + 2;
      placement = "top";
    }

    setPopupPos({
      x: rect.left + rect.width / 2,
      y,
      placement,
    });
    setShowLinkPopup(true);
  };

  const handleLinkMouseLeave = () => setShowLinkPopup(false);

  /**
   * Handle link div click to enter edit mode
   */
  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Prefill editor with current value before entering edit mode
    if (column.typeId === 6) {
      setEditValueImage(String(displayValue ?? ""));
    } else {
      setEditValue(hyperlinkDraftRef.current);
    }
    setIsEditing(true);
    setShowLinkPopup(false);
  };

  const handleRename = React.useCallback((oldValue: string, newValue: string) => {
    const tableId = column.parentField?.fieldid || 0;
    renameDocument(
      String(oldValue),
      String(newValue),
      Number(column.id),
      Number(dataItem.fieldContext || 1),
      Number(dataItem.globalId || 0),
      Number(tableId),
      Number((dataItem as any)?.rrn || 0)
    ).then(() => {
      // Do not write async rename responses back into editor state.
      // They can arrive late and overwrite in-progress user typing.
      // Cell value save (onCellValueChange) is handled synchronously by the Tab/Enter caller.
    }).catch((err) => {
      console.error('Rename failed:', err);
    });
  }, [column.parentField, column.id, dataItem.fieldContext, dataItem.globalId, (dataItem as any)?.rrn, renameDocument]);

  /**
   * Handle link div mouse down to enter edit mode
   */
  const handleLinkMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    notifyBeginEdit();
    if (column.typeId === 6) {
      setEditValueImage(String(displayValue ?? ""));
    } else {
      setEditValue(hyperlinkDraftRef.current);
    }
    setIsEditing(true);
    setShowLinkPopup(false);
  };

  /**
   * Check if we should show dropdown - when there's data OR currently editing (so Enter opens dropdown for empty cells)
   */
  const shouldShowDropdown = React.useMemo(() => {
    return (
      (hasAnyData || isEditing) &&
      column.typeId === 5 &&
      dataDropdownOptions &&
      dataDropdownOptions.length > 0
    );
  }, [hasAnyData, isEditing, column.typeId, dataDropdownOptions]);

  // Ensure react-select gains focus when editing a dropdown cell (double-click or Enter)
  React.useEffect(() => {
    if (isEditing && shouldShowDropdown && selectRef.current) {
      try { selectRef.current.focus(); } catch { }
    }
  }, [isEditing, shouldShowDropdown]);

  // Also focus the inner input of react-select to guarantee arrow key handling
  React.useEffect(() => {
    if (isEditing && column.typeId === 5) {
      try {
        const input = cellContainerRef.current?.querySelector('.ss-cell-select__input input') as HTMLInputElement | null;
        input?.focus();
      } catch { }
    }
  }, [isEditing, column.typeId]);

  // Keep focus on hyperlink editor input while this cell remains in edit mode.
  React.useEffect(() => {
    if (isEditing && column.typeId === 14) {
      const input = hyperlinkInputRef.current;
      if (input && document.activeElement !== input) {
        try {
          input.focus();
          const len = input.value.length;
          input.setSelectionRange(len, len);
        } catch { }
      }
    }
  }, [isEditing, column.typeId]);

  // Stable source of truth for hyperlink/email draft during focus changes.
  React.useEffect(() => {
    if (!isEditing && column.typeId === 14) {
      hyperlinkDraftRef.current = String(displayValue ?? "");
    }
  }, [isEditing, column.typeId, displayValue]);

  /**
   * Check if we should show date picker - also when editing (so Enter opens datepicker for empty cells)
   */
  const shouldShowDatePicker = React.useMemo(() => {
    return (hasAnyData || isEditing) && column.typeId === 2;
  }, [hasAnyData, isEditing, column.typeId]);

  /**
   * Check if this is a hyperlink field (either contains http/https or fieldType is "Hyperlink")
   */
  const isLink = React.useMemo(() => {
    if (column.typeId === 14) {
      // Check if value starts with https or www
      if (displayValue && typeof displayValue === "string") {
        const lowerValue = displayValue.toLowerCase();
        return lowerValue.startsWith("https") || lowerValue.startsWith("www");
      }
    }
    return false;
  }, [column.typeId, displayValue]);

  /**
   * Check if the link value is an email address
   */
  const isEmail = React.useMemo(() => {
    if (!displayValue || typeof displayValue !== "string") return false;
    return displayValue.includes("@");
  }, [displayValue]);

  /**
   * Check if this is a plain text value (not email, not web link) - show Open/Download popup
   */
  const isPlainText = React.useMemo(() => {
    // Only show Open/Download for hyperlink fields that are neither email nor web link
    return column.typeId === 14 && !isEmail && !isLink && displayValue;
  }, [column.typeId, isEmail, isLink, displayValue]);

  const handleOpenClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (dataItem.globalId && dataItem.fieldContext && column.id) {
      try {
        const tableId = column.parentField?.fieldid || 0;
        await openDocument(
          String(displayValue),
          column.id,
          dataItem.fieldContext,
          dataItem.globalId,
          tableId,
          dataItem.rrn || 0
        );
      } catch (error) {
        console.error("Failed to open document:", error);
        // You might want to show an error message to the user here
      }
    } else {
      console.warn("Missing required data for open API call:", {
        globalId: dataItem.globalId,
        fieldContext: dataItem.fieldContext,
        fieldId: column.id,
        displayValue
      });
    }
    setShowLinkPopup(false);
  };

  /**
   * Handle opening email links - prevents logout by temporarily overriding beforeunload
   * This is necessary because some browsers may trigger beforeunload when opening mailto links
   */
  const handleOpenEmail = React.useCallback((mailtoUrl: string) => {
    // Store original beforeunload handler
    const originalBeforeUnload = window.onbeforeunload;
    let mailtoClickInProgress = true;
    const startTime = Date.now();
    const PROTECTION_DURATION = 5000; // 5 seconds to cover delayed beforeunload events

    // Override beforeunload temporarily to prevent logout
    window.onbeforeunload = function (e: BeforeUnloadEvent) {
      // Only suppress if this is a recent mailto click (within protection window)
      if (mailtoClickInProgress && (Date.now() - startTime < PROTECTION_DURATION)) {
        return undefined; // Prevent logout, no confirmation dialog
      }
      // For other cases, use original handler if it exists
      if (originalBeforeUnload) {
        return originalBeforeUnload.call(window, e);
      }
      return undefined;
    };

    // Use window.open for mailto links - this matches the approach in ProgenyHyperlinkFieldEditor.java
    // window.open with mailto doesn't navigate the current page, it just opens the email client
    window.open(mailtoUrl, '_blank');

    // Restore original handler after protection period
    setTimeout(() => {
      mailtoClickInProgress = false;
      window.onbeforeunload = originalBeforeUnload;
    }, PROTECTION_DURATION);
  }, []);

  const handleDownloadClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (dataItem.globalId && dataItem.fieldContext && column.id) {
      try {
        const tableId = column.parentField?.fieldid || 0;
        await downloadDocument(
          String(displayValue),
          column.id,
          dataItem.fieldContext,
          dataItem.globalId,
          tableId,
          dataItem.rrn || 0
        );
      } catch (error) {
        console.error("Failed to download document:", error);
        // You might want to show an error message to the user here
      }
    } else {
      console.warn("Missing required data for download API call:", {
        globalId: dataItem.globalId,
        fieldContext: dataItem.fieldContext,
        fieldId: column.id,
        displayValue
      });
    }
    setShowLinkPopup(false);
  };

  /**
   * Get the appropriate link text for the popup
   */
  const getLinkText = React.useMemo(() => {
    if (!displayValue) return "";

    if (isEmail) {
      return `${displayValue}`;
    } else {
      return I18n.t("Browse to {value}", { value: String(displayValue) });
    }
  }, [displayValue, isEmail]);

  /**
   * Get the appropriate link action (mailto: or http://)
   */
  const getLinkHref = React.useMemo(() => {
    if (!displayValue) return "#";

    if (isEmail) {
      return `mailto:${displayValue}`;
    } else {
      // Ensure URL has protocol
      return displayValue;
    }
  }, [displayValue, isEmail]);

  /**
   * Get dropdown options formatted for Select component
   * Maps dropdown options to the format expected by Select component
   */
  const getDropdownOptions = React.useMemo(() => {
    if (column.typeId === 5 && dataDropdownOptions) {
      const mappedOptions = dataDropdownOptions.map((option) => ({
        value: option.displayValue ?? "",
        label: option.displayValue ?? "",
      }));
      return addBlankOptionToDropdownOptions(mappedOptions);
    }
    return addBlankOptionToDropdownOptions([]);
  }, [column.typeId, dataDropdownOptions]);

  /**
   * Get background color based on modulo and parent field
   */
  const getBackgroundColor = (): string => {
    // Highest priority: row highlighting using new row selection state
    const isRowHighlighted = rowSelectionState && isRowSelected(rowIndex, rowSelectionState);

    // Priority 1: Check if this row is highlighted in row selection mode
    if (isRowHighlighted) {
      return "#cfe2fa"; // Row highlight color (light blue background, no border)
    }

    // Priority 2: Check if this cell is selected (for cell selection highlighting)
    if (isSelected) {
      return "#cfe2fa"; // Cell selection highlight color (light blue background)
    }

    // Do not override the first (row count) column so its CSS class color remains
    if (column.id === SPREADSHEET_TABLE_CONSTANTS.ROW_COUNT_FIELD_ID) {
      return "";
    }

    // Preserve modulo-based colors even if readonly
    if (parentFieldName && hasAnyData) {
      if (modulo === 0) return "#C1FFC1"; // green
      if (modulo === 1) return "yellow"; // yellow
    }

    // Readonly fallback tint (only if not selected)
    if (!isEditable) {
      return "#f5f5f5";
    }

    return "";
  };

  /**
   * Content mapping for different column types
   * Defines how to render content based on column type (static vs text)
   */
  const contentMap: Record<SpreadsheetTableColumnType, React.ReactNode> = {
    // Static columns: Read-only text with ellipsis for overflow
    [SpreadsheetTableColumnType.static]: (
      <Ellipsis
        text={displayValue}
        className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__text-default"
      />
    ),
    // Text columns: Editable content with conditional dropdown, date picker, checkbox, and link support
    [SpreadsheetTableColumnType.text]:
      // Show checkbox if this is a checkbox field
      isCheckboxField ? (
        <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__checkbox-container">
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ padding: "2px 6px" }}

          >
            <input
              type="checkbox"
              checked={isCheckboxChecked}

              onChange={(e) => {
                handleCheckboxChange(e.target.checked);
              }}
              disabled={!isEditable}
              className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__checkbox"
              title={`${isCheckboxChecked ? "Yes" : "No"} (${isCheckboxChecked ? "Y" : "N"})`}
            />
          </div>
        </div>
      ) : // Show dropdown if we should show dropdown
        shouldShowDropdown ? (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "start" }}
            onMouseDown={(e) => {
              e.stopPropagation();
              notifyBeginEdit();
              setShowLookupTableDropdown(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                setIsEditing(false);
                window.dispatchEvent(new CustomEvent('spreadsheet:tabFromCell', {
                  detail: { rowIndex, columnId: uiColumnId, shiftKey: e.shiftKey, spreadsheetId: props.spreadsheetId },
                }));
              }
              e.stopPropagation();
            }}
          >
            <div style={{ width: "100%" }}>
              <Select
                ref={selectRef}
                classNamePrefix="ss-cell-select"
                className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__dropdown"
                options={getDropdownOptions}
                isSearchable={true}
                value={getDropdownOptions.find((o: any) => o.value === displayValue) || null}
                onChange={(opt: any) => {
                  handleDropdownChange(opt?.value || '');
                  setIsEditing(false);
                }}
                isDisabled={!isEditable}
                menuPortalTarget={document.body}
                styles={{
                  menu: base => ({
                    ...base,
                    marginTop: -8,
                  }),
                }}
                autoFocus={true}
                openMenuOnFocus={true}
                menuIsOpen={forceOpenDropdown ? true : undefined}
                onMenuClose={() => setForceOpenDropdown(false)}
                menuPlacement="bottom"
                defaultInputValue={dropdownInitialInput}
                onInputChange={() => { if (dropdownInitialInput) setDropdownInitialInput(''); }}
              />
            </div>
          </div>
        ) : shouldShowDatePicker ? (
          // Show date picker for date fields
          <div onMouseLeave={() => {
            let isSelected = isCellSelected(rowIndex, uiColumnId);
            if (isSelected && column.typeId === 2) {
              let isValid = isValidDate(datePickerRawValue as string)
              if (!isValid) {
                if (showMessage) {
                  showMessage({
                    header: informationMessageHeader,
                    content: getSpreadsheetInvalidDateMessageContent(),
                    size: 'small',
                    onCustomOk: () => {
                      setDatePickerRawValue(displayValue || "");
                      closeMessage();
                    },
                  });
                }
              }
              else {
                if((displayValue!==datePickerRawValue)&&((displayValue!==""||datePickerRawValue!==""))){
                  handleDateValidationAndSave(datePickerRawValue as string);
                }
                }

            }
          }}>

            <DatePicker
              selected={displayValue ? moment(displayValue, "MM/DD/YYYY", true).isValid() ? moment(displayValue, "MM/DD/YYYY", true).toDate() : null : null}
              onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                const target = event.target as HTMLInputElement;
                const saveDateAndMaybeNav = () => {
                  if (target && target.value !== undefined) {
                    const rawValue = target.value;
                    const validation = validateSpreadsheetDateInput(rawValue);
                    if (validation.isValid && validation.normalizedValue !== undefined) {
                      if (onCellValueChange) {
                        onCellValueChange(rowIndex, uiColumnId, validation.normalizedValue);
                      }
                      setForceOpenDate(false);
                      return true;
                    }
                    if (showMessage) {
                      showMessage({
                        header: informationMessageHeader,
                        content: getSpreadsheetInvalidDateMessageContent(),
                        size: 'small',
                      });
                    }
                  }
                  return false;
                };
                if (event.key === 'Enter') {
                  event.preventDefault();
                  event.stopPropagation();
                  const saved = saveDateAndMaybeNav();
                  if (saved) {
                    dateKeyDownHandledRef.current = true; // prevent onSelect from also dispatching
                    setIsEditing(false);
                    window.dispatchEvent(new CustomEvent('spreadsheet:enterFromCell', {
                      detail: { rowIndex, columnId: uiColumnId, shiftKey: event.shiftKey, spreadsheetId: props.spreadsheetId },
                    }));
                  }
                } else if (event.key === 'Tab') {
                  event.preventDefault();
                  event.stopPropagation();
                  const saved = saveDateAndMaybeNav();
                  if (saved) {
                    setIsEditing(false);
                    window.dispatchEvent(new CustomEvent('spreadsheet:tabFromCell', {
                      detail: { rowIndex, columnId: uiColumnId, shiftKey: event.shiftKey, spreadsheetId: props.spreadsheetId },
                    }));
                  }
                }
              }}

              onSelect={(date) => {
                if (!date || !(date instanceof Date) || !isEditable) return;

                const formatted = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
                const validation = validateSpreadsheetDateInput(formatted);
                if (onCellValueChange && validation.isValid && validation.normalizedValue !== undefined) {
                  setDatePickerRawValue(formatted);
                  onCellValueChange(rowIndex, uiColumnId, validation.normalizedValue);
                  if (!dateKeyDownHandledRef.current) {
                    // Calendar click or calendar-Enter: onKeyDown didn't handle navigation, so do it here.
                    // Use dateSelectShiftKeyRef to get the real shiftKey (captured before onSelect fires).
                    const shiftKey = dateSelectShiftKeyRef.current;
                    dateSelectShiftKeyRef.current = false;
                    setForceOpenDate(false);
                    setIsEditing(false);
                    // Defer until after React commits the DOM update (DatePicker removal).
                    // Without this, scrollToItem fires before the DOM mutation, then the
                    // browser's scroll restoration overrides it and jumps to the top.
                    setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('spreadsheet:enterFromCell', {
                        detail: { rowIndex, columnId: uiColumnId, shiftKey, spreadsheetId: props.spreadsheetId },
                      }));
                    }, 0);
                  }
                  dateKeyDownHandledRef.current = false; // reset for next interaction
                }
              }}
              dateFormat={DATE_PICKER_FORMAT}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={50}
              scrollableYearDropdown
              onChangeRaw={(e: any) => {
                setDatePickerRawValue(e.target.value);
              }}
              open={forceOpenDate}
              onCalendarClose={() => setForceOpenDate(false)}
            />
          </div>
        ) : (column.typeId === 14 || isLink || isPlainText || isEmail) ? (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "start" }}
            onMouseDown={(e) => {
              e.stopPropagation();
              notifyBeginEdit();
            }}
            onKeyDown={(e) => {
              const targetInputValue =
                e.target instanceof HTMLInputElement ? e.target.value : undefined;
              const currentEditorValue = targetInputValue ?? editValue;
              if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                if (onCellValueChange) {
                  onCellValueChange(rowIndex, uiColumnId, currentEditorValue);
                  setCellToUpdate(null);
                }
                handleRename(displayValue, currentEditorValue);
                setIsEditing(false);
                window.dispatchEvent(new CustomEvent('spreadsheet:tabFromCell', {
                  detail: { rowIndex, columnId: uiColumnId, shiftKey: e.shiftKey, spreadsheetId: props.spreadsheetId },
                }));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                if (onCellValueChange) {
                  onCellValueChange(rowIndex, uiColumnId, currentEditorValue);
                  setCellToUpdate(null);
                }
                handleRename(displayValue, currentEditorValue);
                setIsEditing(false);
                // Enter navigation is handled by table.controller capture-phase listener.
              }
            }}
          >
            <HyperlinkEditor
              inputRef={hyperlinkInputRef as React.RefObject<HTMLInputElement>}
              value={editValue}
              onChange={(newValue: string) => {
                hyperlinkDraftRef.current = newValue;
                setEditValue(newValue);
                setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
              }}
              onBlur={(newValue: string) => {
                // Preserve typed draft across transient focus loss (click/pause/refocus).
                hyperlinkDraftRef.current = newValue;
                setEditValue(newValue);
                setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
              }}
              onUpload={() => setShowUploadWindow(true)}
              autoFocus={true}
              onSubmit={(newValue: string) => {
                hyperlinkDraftRef.current = newValue;
                if (onCellValueChange) {
                  onCellValueChange(rowIndex, uiColumnId, newValue);
                  setCellToUpdate(null);
                }
                handleRename(displayValue, newValue);
                setIsEditing(false);
              }}
            />
          </div>
        )
          :
          isImageField ? (
            <div
              className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
              style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "start" }}
              onMouseDown={(e) => {
                e.stopPropagation();
                notifyBeginEdit();
              }}
              onKeyDown={(e) => {
                const targetInputValue =
                  e.target instanceof HTMLInputElement ? e.target.value : undefined;
                const currentEditorValue = targetInputValue ?? editValueImage;
                if (e.key === 'Tab') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onCellValueChange) {
                    onCellValueChange(rowIndex, uiColumnId, currentEditorValue);
                    setCellToUpdate(null);
                  }
                  handleRename(displayValue, currentEditorValue);
                  setIsEditing(false);
                  window.dispatchEvent(new CustomEvent('spreadsheet:tabFromCell', {
                    detail: { rowIndex, columnId: uiColumnId, shiftKey: e.shiftKey, spreadsheetId: props.spreadsheetId },
                  }));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onCellValueChange) {
                    onCellValueChange(rowIndex, uiColumnId, currentEditorValue);
                    setCellToUpdate(null);
                  }
                  handleRename(displayValue, currentEditorValue);
                  setIsEditing(false);
                  // Enter navigation is handled by table.controller capture-phase listener.
                }
              }}
            >

              <ImageUploadInput
                value={editValueImage}
                onUpload={() => { setShowUploadWindow(true); }}
                onChange={(newValue: string) => {
                  setEditValueImage(newValue);
                  setCellToUpdate({ rowIndex, columnId: uiColumnId, newValue });
                }}
                onSubmit={(newValue: string) => {
                  if (onCellValueChange) {
                    onCellValueChange(rowIndex, uiColumnId, newValue);
                    setCellToUpdate(null);
                  }
                  handleRename(displayValue, newValue);
                  setIsEditing(false);
                }}
                autoFocus={true}
              />
            </div>
          ) : hasAnyData ? (
            isLookupTableField ? (
            <div
              className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
              style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "start" }}
              onMouseDown={(e) => { notifyBeginEdit(); }}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowLookupTableDropdown(false);
                  setIsEditing(false);
                  setCellToUpdate(null);
                  window.dispatchEvent(new CustomEvent('spreadsheet:tabFromCell', {
                    detail: { rowIndex, columnId: uiColumnId, shiftKey: e.shiftKey, spreadsheetId: props.spreadsheetId },
                  }));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowLookupTableDropdown(false);
                  setIsEditing(false);
                  setCellToUpdate(null);
                  window.dispatchEvent(new CustomEvent('spreadsheet:enterFromCell', {
                    detail: { rowIndex, columnId: uiColumnId, shiftKey: e.shiftKey, spreadsheetId: props.spreadsheetId },
                  }));
                }
              }}
            >
                <LookupTableInput
                  value={String(displayValue ?? "")}
                  onOpen={() => { notifyBeginEdit(); setShowLookupTableDropdown(true); }}
                  className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__lookup-table-trigger"
                  style={{ backgroundColor: "white" }}
                  onBlur={() => {
                    setShowLookupTableDropdown(false);
                    setIsEditing(false);
                    setCellToUpdate(null);
                  }}
                />
              </div>
            ) : (
              <FormControl
                style={{
                  backgroundColor: isEditable ? "white" : "transparent",
                }}
                defaultValue={initialCharRef.current != null ? initialCharRef.current : displayValue}
                className={cx(
                  "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__text",
                  {
                    "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__text--invalid":
                      cellHasNumberValidationError,
                  }
                )}
                onChange={(e) => {
                  if (isNumberField) {
                    handleNumberChange(e.target.value);
                  } else {
                    handleTextChange(e.target.value);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    const currentValue = (e.target as HTMLInputElement).value;
                    if (onCellValueChange) {
                      if (isNumberField) {
                        const validation = validateNumberValue(currentValue);
                        if (validation.isValid) {
                          const finalValue = currentValue.trim() === "" ? "" : Number(currentValue);
                          onCellValueChange(rowIndex, uiColumnId, finalValue);
                        }
                        setCellToUpdate(null);
                      } else {
                        onCellValueChange(rowIndex, uiColumnId, currentValue);
                        setCellToUpdate(null);
                      }
                    }
                    setIsEditing(false);
                    // enterFromCell is dispatched by document capture-phase keydown so navigateSelectionOnEnter runs
                  } else if (e.key === 'Tab') {
                    e.preventDefault();
                    e.stopPropagation();
                    const currentValue = (e.target as HTMLInputElement).value;
                    if (onCellValueChange) {
                      if (isNumberField) {
                        const validation = validateNumberValue(currentValue);
                        if (validation.isValid) {
                          const finalValue = currentValue.trim() === "" ? "" : Number(currentValue);
                          onCellValueChange(rowIndex, uiColumnId, finalValue);
                        }
                        setCellToUpdate(null);
                      } else {
                        onCellValueChange(rowIndex, uiColumnId, currentValue);
                        setCellToUpdate(null);
                      }
                    }
                    setIsEditing(false);
                    window.dispatchEvent(new CustomEvent('spreadsheet:tabFromCell', {
                      detail: { rowIndex, columnId: uiColumnId, shiftKey: e.shiftKey, spreadsheetId: props.spreadsheetId },
                    }));
                  }
                }}
                autoFocus={true}
                readOnly={!isEditable}
              />
            )) : null,
  };

  // Get the appropriate content based on column type
  const content =
    contentMap[column.type as SpreadsheetTableColumnType] ||
    contentMap[SpreadsheetTableColumnType.text];


  // handle right click → exit edit mode
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault(); // prevent browser context menu
    setIsEditing(false);
  };
  // Helper to check if click target is inside a dropdown
  const isDropdownElement = React.useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;

    // Check for react-select dropdown menu (portal rendered in body)
    if (target.closest('[class*="react-select"]') ||
      target.closest('[id*="react-select"]') ||
      target.closest('.ss-cell-select')) {
      return true;
    }

    // Check for react-datepicker dropdown
    if (target.closest('.react-datepicker') ||
      target.closest('.react-datepicker__portal') ||
      target.closest('[class*="react-datepicker"]')) {
      return true;
    }

    // Check for lookup table dropdown
    if (target.closest('.lookup-table-dropdown')) {
      return true;
    }

    return false;
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    // console.log(e.target);

    // Don't activate spreadsheet if clicking on a dropdown
    if (!isDropdownElement(e.target) && props.onActivateSpreadsheet) {
      props.onActivateSpreadsheet();
    }
    // Close any other cell's editor when this cell is clicked (SP-12234)
    notifyBeginEdit();

    if (e.detail === 2) {
      // 👇 second click → treat as double click
      if (isEditable || isDropdownEditable) {
        // Prefill editor state for hyperlink/image before entering edit mode
        setIsEditing(true);
        if (isImageField) {
          setEditValueImage(String(displayValue ?? ""));
        } else if (column.typeId === 14) {
          setEditValue(hyperlinkDraftRef.current);
        }
        notifyBeginEdit();
        // setIsEditing(true);
        // After entering edit mode, auto-open the right editor
        setTimeout(() => {
          if (shouldShowDatePicker) {
            setForceOpenDate(true);
          } else if (isLookupTableField) {
            // Let the isEditing effect compute position and open
          } else if (column.typeId === 5) {
            try {
              // Focus react-select control and its input to capture arrow keys
              selectRef.current?.focus?.();
              const input = cellContainerRef.current?.querySelector('.ss-cell-select__input input') as HTMLInputElement | null;
              input?.focus();
            } catch { }
          }
        }, 0);
      }
      return;
    }

    // Check what type of element was clicked
    const target = e.target as HTMLElement;

    // Handle checkbox clicks - toggle value and select cell
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      const newValue = !isCheckboxChecked;
      handleCheckboxChange(newValue);
      onCellMouseDown(rowIndex, uiColumnId, dataItem, column.colName, e);
      return;
    }

    // For other fields, just select the cell
    onCellMouseDown(rowIndex, uiColumnId, dataItem, column.colName, e);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    onCellMouseEnter(rowIndex, uiColumnId);

    if (hasRequiredFieldError && cellContainerRef.current) {
      const rect = cellContainerRef.current.getBoundingClientRect();
      const tooltipWidth = 180;
      const halfWidth = tooltipWidth / 2;
      let centerX = rect.left + rect.width / 2;
      centerX = Math.max(halfWidth + 8, Math.min(centerX, window.innerWidth - halfWidth - 8));
      const preferAbove = rect.top > 80;
      setRequiredErrorTooltipPos({
        left: centerX,
        top: preferAbove ? rect.top - 8 : rect.bottom + 8,
        placement: preferAbove ? "above" : "below",
      });
    }

    if (isLookupTableField) {
      const rect = e.currentTarget.getBoundingClientRect();
      setPopupPos({
        x: rect.left + rect.width / 2,
        y: rect.bottom + 5,
        placement: "bottom",
      });
    }
  };

  const handleMouseLeave = () => {
    setRequiredErrorTooltipPos(null);
  };

  // Image preview handlers
  const handleImageMouseEnter = (e: React.MouseEvent<HTMLSpanElement | HTMLDivElement>) => {
    if (!isImageField || !imagePreviewUrl) return;
    const mouseX = e.clientX;
    let mouseY = e.clientY + 8; // show just below cursor by default
    let placement: "bottom" | "top" = "bottom";
    const estH = 110; // preview box height estimate
    if (mouseY + estH > window.innerHeight) {
      mouseY = e.clientY - estH - 8; // flip above cursor if overflow
      placement = "top";
    }
    setImagePreviewPos({ x: mouseX + 12, y: mouseY, placement });
    setShowImagePreview(true);
  };

  const handleImageMouseLeave = () => {
    setShowImagePreview(false);
  };

  const handleImagePreviewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isImageField || !imagePreviewUrl) return;
    setShowImageLightbox(true);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    onCellMouseUp();
  };

  const cellBackground = hasCellValidationError
    ? "#FFCCCC"
    : getBackgroundColor();

  return (
    <>
      <MessageBox
        hideMessageBox={closeMessage}
        {...messageProps}
      />
      <div
        ref={cellContainerRef}
        style={{
          minWidth: columnWidth,
          width: columnWidth,
          maxWidth: columnWidth,
          backgroundColor: cellBackground,
        }}
        title={cellTitle}
        className={cx(
          "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell",
          className,
          {
            editable: isEditable,
            readonly: !isEditable,
            "checkbox-field": isCheckboxField,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--invalid":
              hasCellValidationError,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--selected":
              isSelected,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--border-top":
              borderPosition.top,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--border-right":
              borderPosition.right,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--border-bottom":
              borderPosition.bottom,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--border-left":
              borderPosition.left,
            "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell--fill-handle-cell":
              !isEditing && borderPosition.bottom && borderPosition.right,
          }
        )}
        data-row-index={rowIndex}
        data-column-id={uiColumnId}
        onMouseDown={isEditing ? undefined : handleMouseDown}
        onMouseEnter={isEditing ? undefined : handleMouseEnter}
        onMouseLeave={isEditing ? undefined : handleMouseLeave}
        onMouseUp={isEditing ? undefined : handleMouseUp}
        onContextMenu={handleContextMenu}
      >
        {isEditing ? (
          content
        ) : (column.typeId === 14 || isLink || isPlainText || isEmail) ? (
          <div
            id={linkDivId}
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__text-link"
            style={{ width: "100%", height: "100%", margin: "2px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderRadius: "4px", textAlign: "left" }}
            onMouseEnter={handleLinkMouseEnter}
            onMouseLeave={handleLinkMouseLeave}
            onDoubleClick={handleLinkClick}
          // onMouseDown={handleLinkMouseDown}
          >
            {displayValue}
          </div>
        ) : isCheckboxField ? (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ padding: "2px 6px" }}

          >
            <input
              type="checkbox"
              checked={isCheckboxChecked}
              onChange={(e) => {
                e.stopPropagation();
                handleCheckboxChange(e.target.checked);
              }}
              disabled={!isEditable}
              className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__checkbox SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__checkbox--view"
              title={isCheckboxChecked ? I18n.t("Yes (Y)") : I18n.t("No (N)")}
            />
          </div>
        ) : isImageField ? (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ width: "100%", height: "100%", margin: "2px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderRadius: "4px", textAlign: "left", cursor: imagePreviewUrl ? "zoom-in" : "default" }}
            onMouseEnter={handleImageMouseEnter}
            onMouseLeave={handleImageMouseLeave}
            onDoubleClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              notifyBeginEdit();
              setEditValueImage(String(displayValue ?? ""));
              setIsEditing(true);
            }}
            onClick={imagePreviewUrl ? handleImagePreviewClick : undefined}
          >
            {displayValue}
          </div>
        ) : (
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__content-inner"
            style={{ width: "100%", height: "100%", margin: "2px 10px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", borderRadius: "4px", textAlign: "left" }}
          >
            {displayValue}
          </div>
        )}
        {/* Fill handle — Google Sheets style */}
        {!isEditing && borderPosition.bottom && borderPosition.right && (
          <div className="fill-handle" />
        )}
      </div>

      {/* Render popup as portal to document body */}
      {showLinkPopup &&
        column.typeId === 14 &&
        Boolean(displayValue) &&
        createPortal(
          <div
            className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup"
            style={{
              position: "fixed",
              left: popupPos.x,
              top: popupPos.y,
              transform: "translateX(-50%)",
              zIndex: 1000,
            }}
            onMouseEnter={() => setShowLinkPopup(true)}
            onMouseLeave={() => setShowLinkPopup(false)}
          >
            {isPlainText ? (
              // Show editable input with Open/Download links for plain text
              <>
                {/* <div style={{ padding: "4px 6px", fontSize: 12 }}>{displayValue}</div> */}
                <div>
                  <div
                    onClick={handleOpenClick}
                    className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
                  >
                    {I18n.t("Open")}
                  </div>
                  <div
                    onClick={handleDownloadClick}
                    className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
                  >
                    {I18n.t("Download")}
                  </div>
                </div>
              </>
            ) : (
              // Show original link behavior for emails and web links
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (isEmail) {
                    handleOpenEmail(getLinkHref);
                  } else {
                    window.open(getLinkHref, '_blank', 'noopener,noreferrer');
                  }
                }}
                className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__anchor"
              >
                {getLinkText}
              </div>
            )}
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__link-popup__arrow" />
          </div>,
          document.body
        )}

      {/* Image hover preview */}
      {showImagePreview && isImageField && imagePreviewUrl &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: imagePreviewPos.x,
              top: imagePreviewPos.y,
              zIndex: 1000,
              background: "#fff",
              border: "1px solid #ccc",
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              padding: 6,
              borderRadius: 4,
              cursor: 'pointer'
            }}
            title={I18n.t("Click to view full image for {value}", {
              value: String(displayValue),
            })}
            onMouseEnter={() => setShowImagePreview(true)}
            onMouseLeave={() => setShowImagePreview(false)}
            onClick={handleImagePreviewClick}
          >
            <img
              src={imagePreviewUrl}
              alt={String(displayValue || "preview")}
              style={{ maxWidth: 140, maxHeight: 100, display: "block", objectFit: "contain" }}
            />
          </div>,
          document.body
        )}

      {/* Image lightbox */}
      {showImageLightbox && isImageField && imagePreviewUrl &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.6)",
              zIndex: 2000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setShowImageLightbox(false)}
          >
            <img
              src={imagePreviewUrl}
              alt={String(displayValue || "image")}
              style={{
                maxWidth: "85vw",
                maxHeight: "85vh",
                boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                borderRadius: 6,
                background: "#fff",
              }}
            />
          </div>,
          document.body
        )}

      {/* Number validation error tooltip */}
      {cellHasNumberValidationError &&
        numberErrorTooltipPos &&
        createPortal(
          <div
            className={cx(
              "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip",
              {
                "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip--above":
                  numberErrorTooltipPos.placement === "above",
                "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip--below":
                  numberErrorTooltipPos.placement === "below",
              }
            )}
            style={{
              top: numberErrorTooltipPos.top,
              left: numberErrorTooltipPos.left,
            }}
          >
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip__content">
              {numberValidationError}
            </div>
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip__arrow" />
          </div>,
          document.body
        )}

      {/* Required field error tooltip */}
      {hasRequiredFieldError &&
        requiredErrorTooltipPos &&
        createPortal(
          <div
            className={cx(
              "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip",
              "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip--required-message",
              {
                "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip--above":
                  requiredErrorTooltipPos.placement === "above",
                "SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip--below":
                  requiredErrorTooltipPos.placement === "below",
              }
            )}
            style={{
              top: requiredErrorTooltipPos.top,
              left: requiredErrorTooltipPos.left,
            }}
          >
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip__content">
              {I18n.t("This field is required")}
            </div>
            <div className="SpreadsheetDetailsSpreadsheetResultTable__body__row__cell__number-error-tooltip__arrow" />
          </div>,
          document.body
        )}

      {/* Lookup Table Dropdown */}
      {isLookupTableField && column.lookupValues && (
        <LookupTableDropdown
          isOpen={showLookupTableDropdown}
          onClose={() => setShowLookupTableDropdown(false)}
          lookupValues={column.lookupValues}
          onSelect={handleLookupTableSelection}
          position={popupPos}
          selectedValue={String(displayValue || '')}
          onTabNavigation={(shiftKey: boolean) => {
            setShowLookupTableDropdown(false);
            setIsEditing(false);
            setCellToUpdate(null);
            window.dispatchEvent(new CustomEvent('spreadsheet:tabFromCell', {
              detail: { rowIndex, columnId: uiColumnId, shiftKey, spreadsheetId: props.spreadsheetId },
            }));
          }}
          onEnterNavigation={(shiftKey: boolean) => {
            setShowLookupTableDropdown(false);
            setIsEditing(false);
            setCellToUpdate(null);
            window.dispatchEvent(new CustomEvent('spreadsheet:enterFromCell', {
              detail: { rowIndex, columnId: uiColumnId, shiftKey, spreadsheetId: props.spreadsheetId },
            }));
          }}
        />
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadWindow}
        onClose={() => setShowUploadWindow(false)}
        onUpload={handleFileUpload}
        onReject={handleFileReject}
        title={isImageField ? I18n.t("Upload Image") : I18n.t("Upload Document")}
        accept={
          isImageField
            ? [".jpg", ".jpeg", ".gif", ".png", ".bmp"]
            : column.typeId === 14
              ? undefined // allow all types; blacklist handled in handleFileUpload
              : undefined
        }
        maxSize={10 * 1024 * 1024} // 10MB
        multiple={false}
      />
    </>
  );
});
